import { extend, Notify, openURL, uid as genUid } from 'quasar';
import { Store as $store } from '@store';
import { useSubscription } from '@vue/apollo-composable';
import { notificationCreated } from '@graphql/subscriptions/notificationCreated';
import { getUserToken } from 'boot/getUserToken';
import { computed, watch } from 'vue';
import { escape as escapeHtml } from 'lodash-es';

export const deleteNotification = (uid) => {
  $store.commit('notifications/remove', uid);
};

export const markAsRead = (uid) => {
  $store.commit('notifications/markAsRead', uid);
};

export const markAsUnread = (uid) => {
  $store.commit('notifications/markAsUnread', uid);
};

export const setReadStatus = (uid, status) => {
  $store.commit('notifications/setReadStatus', { uid, status });
};

export const broadcastChannel = new BroadcastChannel('app-notifications');

export const notificationTypes = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  TASK: 'task',
  EXPORT: 'export',
};

export const downloadNotificationExport = async (notification) => {
  const xhr = new XMLHttpRequest();
  let { link } = notification;
  if (link.startsWith('/')) {
    link = process.env.SERVER_URI + link;
  }
  // The request carries the bearer token, so only send it to our own API
  if (new URL(link).origin !== new URL(process.env.SERVER_URI).origin) {
    return;
  }
  xhr.open('GET', link);
  xhr.setRequestHeader('Authorization', `Bearer ${await getUserToken()}`);
  xhr.responseType = 'arraybuffer';

  // eslint-disable-next-line func-names
  xhr.onload = function () {
    if (this.status === 200) {
      const url = URL.createObjectURL(new Blob([xhr.response], { type: 'application/octet-stream' }));
      const aTag = document.createElement('a');
      const fileName = notification?.meta?.export_filename || 'export.csv';
      aTag.style.display = 'none';
      aTag.href = url;
      aTag.rel = 'noopener';
      aTag.target = '_blank';
      aTag.download = fileName;
      document.body.appendChild(aTag);
      aTag.click();
      document.body.removeChild(aTag);
    }
  };
  xhr.send();
};

/**
 * App notification
 *
 * @param {Object} notifyOptions
 * @param {string} notifyOptions.id Notification ID
 * @param {string} notifyOptions.type Type of notification: info|warning|error|task
 * @param {string} notifyOptions.title Notification title
 * @param {string} notifyOptions.message Notification message
 * @param {string=} notifyOptions.link Absolute or relative link
 * @param {string=} notifyOptions.linkTitle Text of link
 * @param {Array=} notifyOptions.actions Array of action objects { label:'' , handler() }
 * @param {JSON=} notifyOptions.meta Additional meta information for notifications
 */
export const notify = (notifyOptions) => {
  const notification = extend(
    true,
    {
      uid: genUid(),
      type: notificationTypes.INFO,
      title: 'Notification',
      message: null,
      link: null,
      linkTitle: null,
      date: Date.now(),
      read: false,
      actions: [],
      meta: null,
      position: 'bottom-right',
    },
    notifyOptions,
  );
  const onHide = (n) => {
    // Hide notification from other windows/tabs
    broadcastChannel.postMessage({ id: n.id, action: 'hide' });
  };

  // Assign action buttons
  if (notification.link && notification.linkTitle) {
    notification.actions.push({
      label: notification.linkTitle,
      handler: async () => {
        if (notification.type === notificationTypes.EXPORT) {
          await downloadNotificationExport(notification);
        } else {
          openURL(notification.link);
          markAsRead(notification.uid);
        }
        onHide(notification);
      },
      // noDismiss: true,
    });
  }

  if (
    [notificationTypes.INFO, notificationTypes.WARNING, notificationTypes.ERROR, notificationTypes.EXPORT].includes(
      notification.type,
    )
  ) {
    notification.actions.push({
      label: 'Hide',
      handler: () => {
        markAsRead(notification.uid);
        onHide(notification);
      },
    });
  }

  if (notification.type === notificationTypes.TASK) {
    // TODO: temporary until backend notifications & tasks are ready
    notification.actions.push(
      {
        label: 'Snooze',
        handler: () => {
          markAsRead(notification.uid);
          onHide(notification);
        },
      },
      {
        label: 'Mark as done',
        handler: () => {
          markAsRead(notification.uid);
          onHide(notification);
        },
      },
      {
        label: 'Mark as read',
        handler: () => {
          markAsRead(notification.uid);
          onHide(notification);
        },
      },
    );
  }

  const overrides = {
    type: `app-${notification.type}`,
  };

  // Notifications render as HTML and their text can come from other users, so escape it
  overrides.message = escapeHtml(notification.message ?? '');

  // Add title to main message box
  if (notification.title) {
    overrides.message = `
        <div class="text-weight-medium q-mb-xs">${escapeHtml(notification.title)}</div>
        ${overrides.message}
    `;
  }

  return Notify.create({ ...notification, ...overrides });
};

export const useNotifications = () => {
  const profileId = computed(() => $store.getters['app/user'].id);
  const variables = computed(() => ({ user_id: profileId.value }));

  const { result, restart, start, stop } = useSubscription(
    notificationCreated,
    variables,
  );

  watch(result, () => {
    const notification = result.value?.notificationCreated;
    if (notification) {
      const dismiss = notify({
        id: notification.id,
        type: notificationTypes[notification.type],
        title: notification.title,
        message: notification.message,
        link: notification.related_link,
        linkTitle: notification.related_link_title,
        meta: notification.meta,
      });

      // Listen to hide event from other windows/tabs
      broadcastChannel.addEventListener('message', (event) => {
        if (event.data?.action === 'hide' && event.data?.id === notification.id) {
          dismiss();
        }
      });
    }
  }, { lazy: true });

  return {
    result,
    start,
    stop,
    restart,
  };
};
